/*
 * Copyright (c) 2020. Dave van Rijn Development
 */

import {FetchMethod, ResponseData, sendFetch, setJwt} from '@dvrd/dvr-controls';
import {createParamUrl, URLS} from './api';
import {DistancePurpose, VehicleType} from './interfaces';

interface Paginated {
    limit: number;
    page: number;
}

interface Callback {
    callback: (data: ResponseData, response: Response) => void;
}

interface WithData<T> {
    data: T
}

interface WithVehicleID {
    vehicle_id: string;
}

/*
 ================================================
 AUTH
 ================================================
 */

interface LoginData {
    email: string;
    password: string;
}

export function login(params: WithData<LoginData> & Callback) {
    return sendFetch({
        url: URLS.POST.LOGIN,
        method: FetchMethod.POST,
        ...params,
    });
}

export function logout(params: Callback) {
    return sendFetch({
        url: URLS.GET.LOGOUT,
        callback: (data: ResponseData, response: Response) => {
            setJwt(null);
            params.callback(data, response);
        },
    });
}

/*
 ================================================
 USER
 ================================================
 */

export const getUser = (params: Callback) => {
    sendFetch({
        url: URLS.GET.USER,
        callback: params.callback,
    });
};

/*
 ================================================
 Distance
 ================================================
 */
interface CreateDistance {
    start_km: number;
    end_km: number;
    date: string;
    purpose: DistancePurpose;
    vehicle_id: string;
    description?: string | null;
    driver?: string | null;
}

interface UpdateDistance extends Partial<CreateDistance> {
    distance_id: string;
}

interface DeleteDistance {
    distance_id: string;
}

interface ExportDistances {
    vehicle_id: string;
    start_date?: string;
    end_date?: string;
}

export function createDistance(params: WithData<CreateDistance> & Callback) {
    return sendFetch({
        url: URLS.POST.CREATE_DISTANCE,
        method: FetchMethod.POST,
        ...params
    });
}

export function getDistances(params: WithData<Paginated> & Callback) {
    return sendFetch({
        url: URLS.POST.GET_DISTANCES,
        method: FetchMethod.POST,
        ...params,
    });
}

export function updateDistance(params: WithData<UpdateDistance> & Callback) {
    return sendFetch({
        url: URLS.PUT.UPDATE_DISTANCE,
        method: FetchMethod.PUT,
        ...params,
    });
}

export function deleteDistance(params: DeleteDistance & Callback) {
    return sendFetch({
        url: createParamUrl(URLS.DELETE.DELETE_DISTANCE, {distance_id: params.distance_id}),
        method: FetchMethod.DELETE,
        callback: params.callback,
    });
}

export function exportDistances(params: WithData<ExportDistances> & Callback) {
    return sendFetch({
        url: URLS.POST.EXPORT_DISTANCES,
        method: FetchMethod.POST,
        ...params,
    });
}

export function getLatestKM(params: WithVehicleID & Callback) {
    return sendFetch({
        url: createParamUrl(URLS.GET.LATEST_KM, {vehicle_id: params.vehicle_id}),
        ...params,
    });
}

export function getDistanceInfo(params: WithData<ExportDistances> & Callback) {
    return sendFetch({
        url: URLS.POST.GET_DISTANCE_INFO,
        method: FetchMethod.POST,
        ...params,
    });
}

/*
 ================================================
 Vehicle
 ================================================
 */

interface CreateVehicle {
    label: string;
    licence_number: string;
    vehicle_type: VehicleType;
}

interface UpdateVehicle extends Partial<CreateVehicle> {
    vehicle_id: string;
}

export function createVehicle(params: WithData<CreateVehicle> & Callback) {
    return sendFetch({
        url: URLS.POST.CREATE_VEHICLE,
        method: FetchMethod.POST,
        ...params,
    });
}

export function getVehicles(params: Callback) {
    return sendFetch({
        url: URLS.GET.GET_VEHICLES,
        ...params,
    });
}

export function getVehicle(params: WithVehicleID & Callback) {
    return sendFetch({
        url: createParamUrl(URLS.GET.GET_VEHICLE, {vehicle_id: params.vehicle_id}),
        callback: params.callback,
    });
}

export function updateVehicle(params: WithData<UpdateVehicle> & Callback) {
    return sendFetch({
        url: URLS.PUT.UPDATE_VEHICLE,
        method: FetchMethod.PUT,
        ...params,
    });
}

export function deleteVehicle(params: WithVehicleID & Callback) {
    return sendFetch({
        url: createParamUrl(URLS.DELETE.DELETE_VEHICLE, {vehicle_id: params.vehicle_id}),
        method: FetchMethod.DELETE,
        callback: params.callback,
    });
}